/**
 * El asistente: un ayudante de búsqueda, no un chatbot que conversa.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NO HAY UN MODELO DE LENGUAJE DETRÁS, y es una decisión, no una carencia.
 *
 * Lo que hace es buscar de verdad: manda lo que la persona escribió a
 * `/api/catalogo/asistente`, que relaja la consulta con las reglas del dominio
 * —soltar el calificativo, después cada palabra sola— y prueba cada variante
 * CONTRA EL CATÁLOGO REAL hasta que una devuelve algo.
 *
 * La consecuencia importa: el asistente **no puede nombrar un producto que no
 * vendemos**. Un asistente que sugiere lo que no existe es peor que uno que dice
 * «no lo tengo» — la primera vez que pasa, nadie vuelve a creerle, y en un
 * catálogo industrial el precio de esa equivocación es una faena parada
 * esperando algo que nunca íbamos a poder despachar.
 *
 * Por eso también dice SIEMPRE qué buscó de verdad: «no encontré *teclado
 * lavable de silicona*, pero esto hay en *teclado*». Presentar los resultados de
 * otra consulta como si fueran de la que se pidió es la forma silenciosa de
 * mentir.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Los dos caminos que ofrece ─────────────────────────────────────────────
 *
 *  1. ALTERNATIVAS que existen, con su precio, más las categorías y marcas
 *     donde sí hay algo parecido.
 *  2. PEDIRLO A MEDIDA, que es la solicitud que ya existe, con la opción de
 *     dejar un teléfono para que lo llame alguien del equipo. No inventa una
 *     bandeja nueva: la demanda cae donde el equipo ya mira, en «Lo que nos
 *     piden».
 *
 * ── Por qué la forma de chat ───────────────────────────────────────────────
 *
 * La conversación es guionada y corta —saludo, pregunta, respuesta, dos
 * caminos— y no pretende ser otra cosa. La forma de chat sirve acá porque
 * convierte «no hay resultados» en una pregunta que se puede contestar, en vez
 * de un callejón; no porque haya nada que conversar.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MINIMO_SUGERENCIA, useAsistente, useCrearSolicitud } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { IconoBuscar, IconoCampana, IconoFlecha, IconoMas } from './icons'
import { Money } from './ui'

/** Lo que el asistente responde según qué tan lejos quedó la sugerencia. */
const ENTRADA: Record<'exacta' | 'parcial' | 'familia', (q: string) => string> = {
  exacta: (q) => `Esto es lo que tenemos de «${q}».`,
  parcial: (q) => `No encontré exactamente eso, pero en «${q}» sí hay:`,
  familia: (q) => `Nada calzó completo. De la familia «${q}» tenemos:`,
}

export function Asistente() {
  const { yo, puedeComprar } = useSesion()
  const lugar = useLocation()

  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState('')
  const [consultado, setConsultado] = useState('')
  /** Pantalla del formulario de pedido, con sus datos. */
  const [pidiendo, setPidiendo] = useState(false)
  const [telefono, setTelefono] = useState('')
  const [detalle, setDetalle] = useState('')
  const [enviado, setEnviado] = useState(false)

  const campo = useRef<HTMLInputElement>(null)
  const sugerencia = useAsistente(consultado, abierto)
  const crear = useCrearSolicitud()

  /*
   * Al abrirlo desde el catálogo arranca con lo que la persona ya buscó. Es la
   * diferencia entre un asistente que ayuda y uno que pregunta de nuevo lo que
   * acaba de ver: quien no encontró su teclado ya hizo el esfuerzo de escribirlo.
   */
  useEffect(() => {
    if (!abierto) return
    const q = new URLSearchParams(lugar.search).get('q') ?? ''
    if (q && texto === '') {
      setTexto(q)
      setConsultado(q)
    }
    campo.current?.focus()
    // Solo al abrir: reaccionar a cada cambio de URL pisaría lo que está escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto])

  /*
   * Un proveedor no compra, así que este ayudante no es para él. Un visitante sin
   * sesión SÍ lo ve: es a quien más conviene no dejar sin respuesta, y si llega a
   * pedir algo se le invita a entrar en ese momento.
   */
  if (yo?.rol === 'proveedor') return null

  const datos = sugerencia.data
  const hayAlternativas = (datos?.alternativas.length ?? 0) > 0

  function preguntar(ev: React.FormEvent) {
    ev.preventDefault()
    const limpio = texto.trim()
    if (limpio.length < MINIMO_SUGERENCIA) return
    setConsultado(limpio)
    setPidiendo(false)
    setEnviado(false)
  }

  function pedir(ev: React.FormEvent) {
    ev.preventDefault()
    if (crear.isPending) return
    crear.mutate(
      {
        texto: consultado,
        detalle: detalle.trim() || null,
        telefono: telefono.trim() || null,
        prefiereLlamada: telefono.trim() !== '',
      },
      { onSuccess: () => setEnviado(true) },
    )
  }

  return (
    <>
      {/*
        El disparador va abajo a la derecha y por encima de todo, que es donde se
        busca ayuda sin haberla pedido antes. Se oculta mientras el panel está
        abierto para no tapar su propio contenido en pantallas angostas.
      */}
      {!abierto && (
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className="btn btn-accion fixed bottom-4 right-4 z-40 shadow-none"
        >
          <IconoBuscar size={15} /> ¿No encuentra algo?
        </button>
      )}

      {abierto && (
        <div
          role="dialog"
          aria-label="Asistente de búsqueda"
          className="fixed bottom-4 right-4 z-40 flex max-h-[min(560px,80vh)] w-[360px]
            max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border
            border-acero-300 bg-white"
        >
          <header className="flex items-center gap-2 border-b border-acero-200 bg-acero-50 px-3 py-2">
            <IconoCampana size={15} className="text-acero-500" />
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-[0.09em] text-acero-600">
              Le ayudo a encontrarlo
            </span>
            <button
              type="button"
              onClick={() => setAbierto(false)}
              className="btn btn-fantasma btn-xs"
              aria-label="Cerrar el asistente"
            >
              Cerrar
            </button>
          </header>

          <div className="flex-1 space-y-2.5 overflow-y-auto p-3">
            {/* Lo que dice el asistente va como turno de conversación: una caja
                a la izquierda, siempre. Lo que dice la persona no se repite —ya
                está en el campo de abajo— y repetirlo llenaría el panel. */}
            <p className="rounded-md rounded-bl-none bg-acero-100 px-3 py-2 text-[12.5px] text-acero-800">
              Escriba lo que necesita. Si no lo tenemos, busco lo más parecido y, si tampoco sirve,
              lo pedimos a nuestros proveedores.
            </p>

            {consultado !== '' && sugerencia.isPending && (
              <p className="text-[12px] text-acero-500">Buscando…</p>
            )}

            {datos && !sugerencia.isPending && (
              <>
                <p className="rounded-md rounded-bl-none bg-acero-100 px-3 py-2 text-[12.5px] text-acero-800">
                  {hayAlternativas
                    ? ENTRADA[datos.cercania](datos.consultada)
                    : `No tengo nada parecido a «${consultado}». Pidámoslo a nuestros proveedores.`}
                </p>

                {datos.alternativas.map((p) => (
                  <Link
                    key={p.id}
                    to={`/producto/${encodeURIComponent(p.id)}`}
                    onClick={() => setAbierto(false)}
                    className="flex items-center gap-2 rounded-sm border border-acero-200 px-2.5
                      py-2 hover:border-acero-400"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] text-acero-900">{p.nombre}</span>
                      <span className="block text-[11px] text-acero-500">
                        {p.marca}
                        {!p.hayStock && ' · agotado'}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12.5px] font-semibold text-acero-900">
                      <Money v={p.precioVentaNeto} />
                    </span>
                  </Link>
                ))}

                {/* Las categorías son el camino cuando ninguna alternativa
                    concreta convence: «no es este teclado, muéstrame la
                    categoría». Llevan al catálogo ya filtrado. */}
                {datos.categorias.length > 0 && (
                  <p className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-acero-600">
                    Ver la categoría:
                    {datos.categorias.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/catalogo?q=${encodeURIComponent(datos.consultada)}`}
                        onClick={() => setAbierto(false)}
                        className="chip bg-white hover:border-acero-400"
                      >
                        {c.nombre}
                      </Link>
                    ))}
                  </p>
                )}

                {/*
                  El segundo camino, y el que captura la demanda: pedirlo a
                  medida. Se ofrece SIEMPRE, también cuando hubo alternativas —
                  que ninguna sirva es justamente el caso que antes no tenía
                  salida.
                */}
                {!pidiendo && !enviado && (
                  <button
                    type="button"
                    onClick={() => setPidiendo(true)}
                    className="flex w-full items-center gap-2 rounded-sm border border-dashed
                      border-acero-300 px-2.5 py-2 text-left hover:border-acero-400"
                  >
                    <IconoMas size={14} className="shrink-0 text-acero-500" />
                    <span className="flex-1 text-[12px] text-acero-700">
                      {hayAlternativas
                        ? '¿Ninguno sirve? Pídalo a medida'
                        : 'Pedirlo a nuestros proveedores'}
                    </span>
                    <IconoFlecha size={13} className="shrink-0 text-acero-400" />
                  </button>
                )}

                {pidiendo && !enviado && (
                  <form
                    onSubmit={pedir}
                    className="space-y-2 rounded-sm border border-acero-200 bg-acero-50 p-2.5"
                  >
                    {!yo ? (
                      /*
                        Sin sesión el backend rechaza la solicitud con 401. Se
                        dice acá, antes de que llene el formulario: descubrirlo
                        al enviar es perder el texto y a la persona.
                      */
                      <p className="text-[12px] text-acero-700">
                        Para registrar el pedido necesito saber de qué empresa es.{' '}
                        <Link to="/entrar" className="text-marca-600 hover:underline">
                          Entre a su cuenta
                        </Link>{' '}
                        y vuelvo a preguntárselo.
                      </p>
                    ) : !puedeComprar ? (
                      <p className="text-[12px] text-acero-700">
                        Su cuenta no está asociada a una empresa compradora, así que no puedo
                        atribuir el pedido. Escríbanos y lo resolvemos.
                      </p>
                    ) : (
                      <>
                        <p className="text-[12px] text-acero-700">
                          Pido <strong>{consultado}</strong>. ¿Algún detalle que ayude —medidas,
                          marca que sirva, para qué equipo es?
                        </p>
                        <textarea
                          className="campo text-[12px]"
                          rows={2}
                          value={detalle}
                          onChange={(e) => setDetalle(e.target.value)}
                          placeholder="Opcional"
                          aria-label="Detalle del pedido"
                        />
                        <label className="block">
                          <span className="etiqueta">Teléfono, si prefiere que lo llamemos</span>
                          <input
                            className="campo text-[12px]"
                            value={telefono}
                            onChange={(e) => setTelefono(e.target.value)}
                            placeholder="Opcional — +56 9 …"
                          />
                        </label>
                        {crear.isError && (
                          <p className="error-msg" role="alert">
                            {crear.error.message}
                          </p>
                        )}
                        <button
                          type="submit"
                          className="btn btn-accion w-full"
                          disabled={crear.isPending}
                        >
                          {crear.isPending ? 'Enviando…' : 'Enviar el pedido'}
                        </button>
                        {/* No se promete lo que el sistema no puede cumplir. */}
                        <p className="ayuda">
                          Queda registrado y no compromete una compra. Si deja teléfono, lo llama
                          alguien del equipo.
                        </p>
                      </>
                    )}
                  </form>
                )}

                {enviado && (
                  <p className="rounded-md rounded-bl-none bg-ok-100 px-3 py-2 text-[12.5px] text-acero-800">
                    Listo, anoté <strong>{consultado}</strong>.
                    {telefono.trim() !== ''
                      ? ' Lo llamamos al número que dejó.'
                      : ' Si lo incorporamos, aparece en el catálogo.'}
                  </p>
                )}
              </>
            )}
          </div>

          <form onSubmit={preguntar} className="flex gap-1.5 border-t border-acero-200 p-2">
            <input
              ref={campo}
              className="campo text-[12.5px]"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="teclado lavable, guante anticorte…"
              aria-label="Qué necesita"
            />
            <button
              type="submit"
              className="btn btn-secundario shrink-0"
              disabled={texto.trim().length < MINIMO_SUGERENCIA}
            >
              Buscar
            </button>
          </form>
        </div>
      )}
    </>
  )
}
